import { Loader2, Package, Save, Sparkles } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { SystemStatusBannerSection } from "src/components/release/SystemStatusBannerSection";
import { SlabButton } from "src/components/ui/SlabButton";
import { actorFromAuth } from "src/lib/activityService";
import { Permission, hasPermission } from "src/lib/roles";
import {
  getVersionControl,
  normalizeVersionControlInput,
  saveVersionControl,
  type VersionControlConfig,
} from "src/lib/versionControlService";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import { Constants } from "../constant";

function versionControlEqual(
  a: VersionControlConfig,
  b: VersionControlConfig,
): boolean {
  return (
    a.minimum_required_build === b.minimum_required_build &&
    a.recommended_build === b.recommended_build &&
    a.force_update === b.force_update &&
    a.update_message === b.update_message &&
    a.reminder_interval_days === b.reminder_interval_days &&
    a.status_banner_enabled === b.status_banner_enabled &&
    a.status_banner_message === b.status_banner_message &&
    a.status_banner_color === b.status_banner_color
  );
}

/** Playful slab UI — teal primary, indigo secondary, amber accents (see `.cursor/rules/playful-slab-ui.mdc`). */
const slab = {
  teal: "#14B8A6",
  tealLip: "#0F766E",
  indigo: "#6366F1",
} as const;

const inputClass =
  "min-h-[48px] w-full rounded-2xl border-2 border-[#E5E5E5] bg-white px-4 py-2.5 text-[15px] font-semibold text-[#3C3C3C] shadow-[inset_0_2px_0_rgba(0,0,0,0.04)] placeholder:font-medium placeholder:text-[#AFAFAF] focus:border-[#6366F1] focus:outline-none focus:ring-4 focus:ring-[#6366F1]/25 disabled:cursor-not-allowed disabled:opacity-60";

const numberInputClass = `${inputClass} tabular-nums`;

const labelClass =
  "flex flex-col gap-2 text-sm font-extrabold uppercase tracking-wide text-[#777777]";

const AppReleaseVersionsPanel: React.FC = () => {
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const activityActor = actorFromAuth(user, userProfile);

  const canView =
    userProfile && hasPermission(userProfile.role, Permission.VIEW_APP_VERSION);
  const canManage =
    userProfile &&
    hasPermission(userProfile.role, Permission.MANAGE_APP_VERSION);

  const [form, setForm] = useState<VersionControlConfig | null>(null);
  const [savedBaseline, setSavedBaseline] =
    useState<VersionControlConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getVersionControl();
      setForm(data);
      setSavedBaseline({ ...data });
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : "Failed to load version control",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  const handleSave = async () => {
    if (
      !canManage ||
      !form ||
      !savedBaseline ||
      versionControlEqual(form, savedBaseline)
    )
      return;
    if (!activityActor) {
      setError(
        "Cannot save without a signed-in user (required for activity log).",
      );
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await saveVersionControl(form, activityActor);
      const next = normalizeVersionControlInput(form);
      setForm(next);
      setSavedBaseline({ ...next });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const updateField = <K extends keyof VersionControlConfig>(
    key: K,
    value: VersionControlConfig[K],
  ) => {
    if (!canManage || !form) return;
    setForm({ ...form, [key]: value });
  };

  if (!canView) {
    return (
      <div
        className="mx-auto max-w-lg py-8 sm:py-10"
        style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
      >
        <div className="rounded-[2rem] border-4 border-[#E5E5E5] bg-white p-8 text-center shadow-[0_8px_0_#D0D0D0] sm:p-10">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FEF3C7] text-3xl ring-2 ring-[#F59E0B]/40">
            🔒
          </div>
          <h1 className="mb-2 text-2xl font-extrabold text-[#3C3C3C] sm:text-3xl">
            Almost there
          </h1>
          <p className="text-base font-semibold leading-relaxed text-[#777777]">
            {Constants.APP_RELEASE_VERSIONS_DESCRIPTION}
          </p>
        </div>
      </div>
    );
  }

  const disabled = !canManage;
  const isDirty =
    form != null &&
    savedBaseline != null &&
    !versionControlEqual(form, savedBaseline);
  const saveDisabled = saving || !isDirty;

  return (
    <div
      className="mx-auto max-w-2xl pb-10 pt-2 sm:max-w-3xl sm:pt-4"
      style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
    >
      <header className="mb-8 text-center sm:mb-10">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#6366F1]/25 px-4 py-1.5 text-sm font-extrabold text-white ring-2 ring-[#6366F1]/45">
          <Sparkles className="h-4 w-4 text-[#A5B4FC]" aria-hidden />
          Mobile clients
        </div>
        <h1 className="mb-2 flex flex-wrap items-center justify-center gap-2 text-3xl font-extrabold tracking-tight text-white drop-shadow-sm sm:gap-3 sm:text-4xl">
          <span
            className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-[0_4px_0_#0F766E] sm:h-14 sm:w-14"
            style={{ backgroundColor: slab.teal }}
          >
            <Package
              className="h-7 w-7 sm:h-8 sm:w-8"
              strokeWidth={2.25}
              aria-hidden
            />
          </span>
          <span className="max-w-[18ch] leading-tight sm:max-w-none">
            App release versions
          </span>
        </h1>
        <p className="mx-auto max-w-md text-base font-bold text-white/80 sm:text-lg">
          Control forced vs optional updates, status banner, and reminder
          cadence. Stored at{" "}
          <code className="rounded-md bg-white/10 px-1.5 py-0.5 font-mono text-sm font-semibold text-[#A5B4FC]">
            app_config/version_control
          </code>
          .
        </p>
      </header>

      {error && (
        <div
          className="mb-6 rounded-[1.5rem] border-4 border-[#FECACA] bg-[#FEF2F2] p-4 shadow-[0_6px_0_#FCA5A5]"
          role="alert"
        >
          <p className="text-center text-sm font-extrabold text-[#B91C1C]">
            {error}
          </p>
        </div>
      )}

      {loading && !form ? (
        <div className="flex flex-col items-center justify-center rounded-[1.75rem] border-4 border-white/10 bg-white/5 py-16">
          <Loader2
            className="h-12 w-12 animate-spin"
            style={{ color: slab.teal }}
            aria-label="Loading"
          />
          <p className="mt-4 font-extrabold text-white/70">
            Loading version policy…
          </p>
        </div>
      ) : !form ? (
        <div className="rounded-[1.75rem] border-4 border-dashed border-white/25 bg-white/5 p-10 text-center">
          <p className="text-lg font-extrabold text-white/90">
            Couldn&apos;t load version control.
          </p>
          <p className="mt-2 font-bold text-white/60">
            {Constants.NETWORK_ERROR_DESCRIPTION}
          </p>
          <button
            type="button"
            onClick={() => load()}
            className="mt-6 inline-flex min-h-[48px] min-w-[8rem] items-center justify-center rounded-2xl border-2 border-[#E5E5E5] bg-white px-6 text-sm font-extrabold text-[#3C3C3C] shadow-[0_4px_0_#D0D0D0] active:translate-y-0.5"
          >
            Retry
          </button>
        </div>
      ) : (
        <section
          className="rounded-[1.75rem] border-4 border-[#E5E5E5] bg-white p-5 shadow-[0_8px_0_#D0D0D0] sm:p-8"
          aria-labelledby="version-policy-heading"
        >
          <h2 id="version-policy-heading" className="sr-only">
            Version policy
          </h2>

          <div className="mb-8 flex items-start gap-3">
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg font-extrabold text-white shadow-[0_4px_0_#4F46E5]"
              style={{ backgroundColor: slab.indigo }}
            >
              1
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="text-xl font-extrabold text-[#3C3C3C] sm:text-2xl">
                Build thresholds
              </h3>
              <p className="mt-1 font-bold text-[#777777]">
                {Constants.APP_RELEASE_THRESHOLDS_DESCRIPTION}
              </p>
              <div className="mt-6 flex flex-col gap-6">
                <label className={labelClass}>
                  Minimum required build
                  <input
                    type="number"
                    min={0}
                    step={1}
                    disabled={disabled}
                    value={form.minimum_required_build}
                    onChange={(e) =>
                      updateField(
                        "minimum_required_build",
                        parseInt(e.target.value, 10) || 0,
                      )
                    }
                    className={numberInputClass}
                  />
                  <span className="normal-case font-semibold text-[#777777]">
                    {Constants.APP_RELEASE_MINIMUM_BUILD_DESCRIPTION}
                  </span>
                </label>

                <label className={labelClass}>
                  Recommended build
                  <input
                    type="number"
                    min={0}
                    step={1}
                    disabled={disabled}
                    value={form.recommended_build}
                    onChange={(e) =>
                      updateField(
                        "recommended_build",
                        parseInt(e.target.value, 10) || 0,
                      )
                    }
                    className={numberInputClass}
                  />
                  <span className="normal-case font-semibold text-[#777777]">
                    {Constants.APP_RELEASE_RECOMMENDED_BUILD_DESCRIPTION}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="my-8 h-px bg-[#E5E5E5]" />

          <div className="mb-8 flex items-start gap-3">
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg font-extrabold text-white shadow-[0_4px_0_#4F46E5]"
              style={{ backgroundColor: slab.indigo }}
            >
              2
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="text-xl font-extrabold text-[#3C3C3C] sm:text-2xl">
                Behavior &amp; copy
              </h3>
              <p className="mt-1 font-bold text-[#777777]">
                {Constants.APP_RELEASE_BEHAVIOR_AND_COPY_DESCRIPTION}
              </p>

              <div className="mt-6 rounded-2xl border-2 border-[#E5E5E5] bg-[#FAFAFA] p-4 sm:p-5">
                <label className="flex min-h-[44px] cursor-pointer items-start gap-3 select-none sm:items-center">
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={form.force_update}
                    onChange={(e) =>
                      updateField("force_update", e.target.checked)
                    }
                    className="mt-1 h-5 w-5 shrink-0 rounded border-2 border-[#E5E5E5] text-[#14B8A6] focus:ring-4 focus:ring-[#6366F1]/25 disabled:opacity-60 sm:mt-0"
                  />
                  <span>
                    <span className="block text-sm font-extrabold uppercase tracking-wide text-[#3C3C3C]">
                      Force update
                    </span>
                    <span className="mt-1 block text-sm font-semibold text-[#777777]">
                      Treat the recommended build as mandatory (same urgency as
                      minimum).
                    </span>
                  </span>
                </label>
              </div>

              <div className="mt-6 flex flex-col gap-6">
                <label className={labelClass}>
                  Update message
                  <textarea
                    rows={3}
                    disabled={disabled}
                    value={form.update_message}
                    onChange={(e) =>
                      updateField("update_message", e.target.value)
                    }
                    placeholder="Shown in the client update dialog"
                    className={`${inputClass} min-h-[5.5rem] resize-y py-3`}
                  />
                </label>

                <label className={labelClass}>
                  Reminder interval (days)
                  <input
                    type="number"
                    min={1}
                    step={1}
                    disabled={disabled}
                    value={form.reminder_interval_days}
                    onChange={(e) =>
                      updateField(
                        "reminder_interval_days",
                        Math.max(1, parseInt(e.target.value, 10) || 1),
                      )
                    }
                    className={numberInputClass}
                  />
                  <span className="normal-case font-semibold text-[#777777]">
                    Minimum days between optional-update reminders in the app.
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="my-8 h-px bg-[#E5E5E5]" />

          <SystemStatusBannerSection
            step={3}
            disabled={disabled}
            enabled={form.status_banner_enabled}
            message={form.status_banner_message}
            color={form.status_banner_color}
            onEnabledChange={(enabled) =>
              updateField("status_banner_enabled", enabled)
            }
            onMessageChange={(message) =>
              updateField("status_banner_message", message)
            }
            onColorChange={(color) => updateField("status_banner_color", color)}
          />

          {canManage && (
            <SlabButton
              variant="primary"
              type="button"
              onClick={handleSave}
              disabled={saveDisabled}
              backgroundColor={slab.teal}
              lipColor="#0F766E"
              shadowLiftPx={6}
              shadowLiftActivePx={2}
              className="w-full rounded-2xl px-6 disabled:opacity-60 sm:w-auto"
            >
              {saving ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-5 w-5" aria-hidden />
                  Save changes
                </>
              )}
            </SlabButton>
          )}
        </section>
      )}
    </div>
  );
};

export default AppReleaseVersionsPanel;
